import { logError } from 'services/Logger';
import { environmentUtils } from 'utils';

export const USAGE_ORDER_KEY = 'usageOrder';

export const loadUsageOrder = (): string[] => {
  try {
    const data = localStorage.getItem(USAGE_ORDER_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    logError('Error loading usage order from local storage:', error);
    return [];
  }
};

let usageOrder = loadUsageOrder();

export const saveUsageOrder = (order: string[]) => {
  try {
    localStorage.setItem(USAGE_ORDER_KEY, JSON.stringify(order));
  } catch (error) {
    logError('Error saving usage order to local storage:', error);
  }
};

export const updateUsageOrder = (key: string) => {
  usageOrder = usageOrder.filter((item) => item !== key);
  usageOrder.push(key);
  saveUsageOrder(usageOrder);
};

const handleQuotaExceededError = <T>(key: string, value: T) => {
  while (usageOrder.length > 0) {
    const oldestKey = usageOrder.shift();
    if (oldestKey) {
      localStorage.removeItem(oldestKey);
    }
    try {
      localStorage.setItem(key, JSON.stringify(value));
      updateUsageOrder(key);
      return;
    } catch (error) {
      if ((error as DOMException).name !== 'QuotaExceededError') {
        logError('Error setting local storage item:', error);
      }
    }
  }
  if (usageOrder.length === 0) {
    localStorage.clear();
    try {
      localStorage.setItem(key, JSON.stringify(value));
      updateUsageOrder(key);
    } catch (error) {
      logError('Error setting local storage item:', error);
    }
  }
};

export const setItem = <T>(
  key: string,
  value: T,
  { shouldStringify = true }: { shouldStringify?: boolean } = {}
): void => {
  try {
    if (!shouldStringify && typeof value === 'string') {
      localStorage.setItem(key, value);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }

    updateUsageOrder(key);
  } catch (error) {
    if ((error as DOMException).name === 'QuotaExceededError') {
      handleQuotaExceededError(key, value);
    } else {
      logError('Error setting local storage item:', error);
    }
  }
};

export const getItem = <T>(key: string): T | null => {
  try {
    const data = localStorage.getItem(key);
    if (data) {
      updateUsageOrder(key);
      return JSON.parse(data) as T;
    }
    return null;
  } catch (error) {
    logError('Error getting local storage item:', error);
    return null;
  }
};

type Dictionary = { [key: string]: string };
export const getItemsByPrefix = (prefix: string, storage = localStorage): Dictionary =>
  Object.entries(storage)
    .filter(([key]) => key.startsWith(prefix))
    .reduce((acc: Dictionary, [key, value]) => {
      acc[key.replace(prefix, '')] = value;
      return acc;
    }, {});

/**
 * Copies an item from sessionStorage (if any) into localStorage under a
 * namespace for the current instance.
 * @param key The storage key
 * @returns {void}
 */
export const persistInLocalStorage = (key: string) => {
  const instance = environmentUtils.getEnvironmentInstance();
  const sessionValue = sessionStorage.getItem(key);

  if (sessionValue) {
    localStorage.setItem(`${instance}:${key}`, sessionValue);
  }
};

/**
 * Copies an item from localStorage into sessionStorage.
 * @param {string} key - The storage key
 * @param {Object} options
 * @param {boolean} [options.override=true] - Whether to copy the value if it is already in sessionStorage
 * @returns {void}
 */
export const copyFromLocalStorage = (key: string, options = { override: true }) => {
  if (sessionStorage.getItem(key) && !options.override) {
    return;
  }

  const instance = environmentUtils.getEnvironmentInstance();
  const localStorageValue = localStorage.getItem(`${instance}:${key}`);
  if (localStorageValue) {
    sessionStorage.setItem(key, localStorageValue);
  }
};
